import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Job } from '@/features/jobs/Job'
import type { AlertErrorHandlerFactory } from '@/features/shell'

/**
 * View model for the dialog that renames a job.
 */
export class RenameJobDialogViewModel {
  /**
   * The name typed by the user; `null` until they type, so the field follows
   * realtime renames of the job until then.
   */
  @observable private accessor _draftName: string | null = null

  readonly modal = new ModalViewModel({ onClose: () => this.setName(null) })

  constructor(
    private readonly _getJob: () => Job,
    private readonly _rename: (newName: string) => Promise<unknown>,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * The job to rename.
   */
  @computed
  get job() {
    return this._getJob()
  }

  /**
   * The name the field shows.
   */
  @computed
  get newName() {
    return this._draftName ?? (this.job.name || this.job.legendName)
  }

  set newName(name: string) {
    this.setName(name)
  }

  /**
   * Whether the name has been changed.
   */
  @computed
  get nameHasChanged() {
    return this.job.name !== this.newName.trim()
  }

  /**
   * Shows the dialog.
   */
  @action.bound
  show() {
    if (this.modal.showing) {
      return
    }

    this.modal.show()
  }

  /**
   * Sets the name.
   *
   * @param name
   */
  @action.bound
  setName(name: string | null) {
    this._draftName = name
  }

  /**
   * Rename the job.
   */
  rename = task.resolved(async () => {
    await this._rename(this.newName.trim()).catch(
      this._createAlertErrorHandler('Renaming the job failed'),
    )
    this.modal.close()
  })
}
